import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../components/Header/Header';
import './ThemeViewer.css';

interface ThemeViewerProps {}

interface Page {
  name: string;
  path: string;
  description?: string;
}

interface Section {
  name: string;
  pages: Page[];
}

interface ProjectData {
  sections: Section[];
}

const ThemeViewer: React.FC<ThemeViewerProps> = () => {
  const params = useParams<{ section?: string; page: string }>();
  const pagePath = params.section ? `${params.section}/${params.page}` : params.page;
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [cssContent, setCssContent] = useState<string>('');
  const [jsContent, setJsContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [projectData, setProjectData] = useState<ProjectData | null>(null);

  useEffect(() => {
    const loadProjectData = async () => {
      try {
        const response = await fetch('/data/pages.json');
        if (response.ok) {
          const data = await response.json();
          setProjectData(data.projects[0]);
        }
      } catch (e) {
        // Project data is optional
      }
    };

    const loadThemeContent = async () => {
      if (!pagePath) return;

      setLoading(true);
      setError('');

      try {
        // Load HTML content - try multiple paths
        let htmlResponse = null;
        const htmlPaths = [
          `/theme/${pagePath}/index.html`,
          `/theme/${pagePath}`,
          `/theme/${pagePath}.html`
        ];

        for (const path of htmlPaths) {
          try {
            htmlResponse = await fetch(path);
            if (htmlResponse.ok) {
              break;
            }
          } catch (e) {
            continue;
          }
        }

        if (!htmlResponse || !htmlResponse.ok) {
          throw new Error(`Failed to load HTML: ${htmlResponse?.statusText || 'Not found'}`);
        }

        const html = await htmlResponse.text();
        setHtmlContent(html);

        // Load CSS content
        const cssFiles = ['styles.css', 'style.css', 'global.css'];
        
        for (const cssFile of cssFiles) {
          try {
            let cssResponse = await fetch(`/theme/${pagePath}/${cssFile}`);
            if (!cssResponse.ok) {
              // Try alternative path for direct directory access
              cssResponse = await fetch(`/theme/${pagePath}/${cssFile}`);
            }
            if (cssResponse.ok) {
              let css = await cssResponse.text();
              
              // Fix image paths in CSS
              css = css.replace(
                /url\(images\//g,
                `url(/theme/${pagePath}/images/`
              );
              css = css.replace(
                /url\(img\//g,
                `url(/theme/${pagePath}/img/`
              );
              css = css.replace(
                /url\(\.\/img\//g,
                `url(/theme/${pagePath}/img/`
              );
              
              setCssContent(css);
              break;
            }
          } catch (e) {
            // Continue to next CSS file
          }
        }

        // Load global CSS files
        try {
          const globalsResponse = await fetch('/theme/globals.css');
          if (globalsResponse.ok) {
            let globals = await globalsResponse.text();
            
            // Fix image paths in global CSS
            globals = globals.replace(
              /url\(images\//g,
              `url(/theme/images/`
            );
            globals = globals.replace(
              /url\(img\//g,
              `url(/theme/img/`
            );
            globals = globals.replace(
              /url\(\.\/img\//g,
              `url(/theme/img/`
            );
            
            setCssContent(prev => prev + '\n' + globals);
          }
        } catch (e) {
          // Global CSS is optional
        }

        try {
          const styleguideResponse = await fetch('/theme/styleguide.css');
          if (styleguideResponse.ok) {
            let styleguide = await styleguideResponse.text();
            
            // Fix image paths in styleguide CSS
            styleguide = styleguide.replace(
              /url\(images\//g,
              `url(/theme/images/`
            );
            styleguide = styleguide.replace(
              /url\(img\//g,
              `url(/theme/img/`
            );
            styleguide = styleguide.replace(
              /url\(\.\/img\//g,
              `url(/theme/img/`
            );
            
            setCssContent(prev => prev + '\n' + styleguide);
          }
        } catch (e) {
          // Styleguide CSS is optional
        }

        // Load JavaScript content
        const jsFiles = ['scripts.js', 'script.js', 'global.js'];
        
        for (const jsFile of jsFiles) {
          try {
            let jsResponse = await fetch(`/theme/${pagePath}/${jsFile}`);
            if (!jsResponse.ok) {
              // Try alternative path for direct directory access
              jsResponse = await fetch(`/theme/${pagePath}/${jsFile}`);
            }
            if (jsResponse.ok) {
              const js = await jsResponse.text();
              setJsContent(js);
              break;
            }
          } catch (e) {
            // Continue to next JS file
          }
        }

      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load theme content');
      } finally {
        setLoading(false);
      }
    };

    loadProjectData();
    loadThemeContent();
  }, [pagePath]);

  const createFullHtml = () => {
    if (!htmlContent) return '';

    // Extract the body content from the HTML
    const bodyMatch = htmlContent.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    let bodyContent = bodyMatch ? bodyMatch[1] : htmlContent;

    // Fix image paths to be absolute
    bodyContent = bodyContent.replace(
      /src="images\//g,
      `src="/theme/${pagePath}/images/`
    );
    bodyContent = bodyContent.replace(
      /src="img\//g,
      `src="/theme/${pagePath}/img/`
    );

    // Extract the head content (for meta tags, title, etc.)
    const headMatch = htmlContent.match(/<head[^>]*>([\s\S]*)<\/head>/i);
    const headContent = headMatch ? headMatch[1] : '';

    return `
      <!DOCTYPE html>
      <html lang="pt-BR">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${pagePath} - Data Stone</title>
          <style>
            ${cssContent}
          </style>
          ${headContent}
        </head>
        <body>
          ${bodyContent}
          ${jsContent ? `<script>${jsContent}</script>` : ''}
        </body>
      </html>
    `;
  };

  if (loading) {
      return (
    <div className="theme-viewer">
      <Header 
        showStats={true}
        sectionCount={projectData?.sections?.length || 0}
        templateCount={projectData?.sections?.reduce((acc: number, section: Section) => acc + section.pages.length, 0) || 0}
        figmaUrl="https://figma.com/file/example"
        githubUrl="https://github.com/uxagency/visao-prev"
        liveUrl="https://visaoprev.com"
        subtitle="Pensando no futuro, cuidando do presente"
        viewingPage={pagePath}
        onBackClick={() => window.location.href = '/'}
        onNewTabClick={() => window.open(`/theme/${pagePath}/index.html`, '_blank')}
      />
      <div className="loading-spinner">
        <div className="spinner"></div>
        <p>Loading theme content...</p>
      </div>
    </div>
  );
  }

  if (error) {
    return (
      <div className="theme-viewer">
        <Header 
          showStats={true}
          sectionCount={projectData?.sections?.length || 0}
          templateCount={projectData?.sections?.reduce((acc: number, section: Section) => acc + section.pages.length, 0) || 0}
          figmaUrl="https://figma.com/file/example"
          githubUrl="https://github.com/uxagency/visao-prev"
          liveUrl="https://visaoprev.com"
          subtitle="Pensando no futuro, cuidando do presente"
          viewingPage={pagePath}
          onBackClick={() => window.location.href = '/'}
          onNewTabClick={() => window.open(`/theme/${pagePath}/index.html`, '_blank')}
        />
        <div className="error-message">
          <p>{error}</p>
          <Link to="/" className="error-back-button">Return to Dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="theme-viewer">
      <Header 
        showStats={true}
        sectionCount={projectData?.sections?.length || 0}
        templateCount={projectData?.sections?.reduce((acc: number, section: Section) => acc + section.pages.length, 0) || 0}
        figmaUrl="https://figma.com/file/example"
        githubUrl="https://github.com/uxagency/visao-prev"
        liveUrl="https://visaoprev.com"
        subtitle="Pensando no futuro, cuidando do presente"
        viewingPage={pagePath}
        onBackClick={() => window.location.href = '/'}
        onNewTabClick={() => window.open(`/theme/${pagePath}/index.html`, '_blank')}
      />
      
      <div className="viewer-content">
        <iframe
          srcDoc={createFullHtml()}
          title={`${pagePath} Template`}
          className="theme-iframe"
          sandbox="allow-scripts allow-same-origin"
        />
      </div>
    </div>
  );
};

export default ThemeViewer; 